import { useFormik, FieldArray, FormikProvider, getIn } from 'formik'
import * as Yup from 'yup'
import styles from './BlockUpload.module.css'
import Close from '../../../assets/close.svg?react'
import DeleteImage from '../../../assets/delete-image.svg?react'
import UploadSuccess from '../../../assets/upload-success.svg?react'
import ArrowLeftSlideShow from '../../../assets/left-slideshow-arrow.svg?react'
import ArrowRightSlideShow from '../../../assets/right-slideshow-arrow.svg?react'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { type Swiper as SwiperRef } from 'swiper'
import 'swiper/css'
import { Monument } from '../../../types'
import { categoryTree, uploadImages } from '../../../hooks/monuments'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useAuthUser } from 'use-eazy-auth'
import { useQueryClient } from '@tanstack/react-query'
import { Modal, Spinner } from 'react-bootstrap'
import classNames from 'classnames'
import { getActiveGeocontext, isBrowserMobile } from '../../../utils'
import { toast } from 'react-toastify'
import { processUploadFiles } from './processUpload'

dayjs.extend(customParseFormat)

function CategoryTree({
  category,
  categoryChildren,
  expandedCategories,
  toggleCategory,
  setCurrentNonWlmCat,
  currentNonWlmCat,
  level = 0,
  originalIndex,
  disabled = false,
}: {
  category: string
  categoryChildren: Record<string, string[]>
  expandedCategories: Record<string, boolean>
  toggleCategory: (originalIndex: number, category: string) => void
  setCurrentNonWlmCat: (category: string) => void
  currentNonWlmCat: string
  level?: number
  originalIndex: number
  disabled?: boolean
}) {
  const isExpanded = !!expandedCategories[category]
  const children = categoryChildren[category] ?? []
  const isSelected = currentNonWlmCat === category
  const hasLoadedChildren = Object.prototype.hasOwnProperty.call(
    categoryChildren,
    category
  )
  const isLeaf = hasLoadedChildren && children.length === 0

  return (
    <div style={{ marginLeft: level * 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          borderRadius: 8,
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <button
          type="button"
          onClick={() => {
            if (!isLeaf && !disabled) {
              toggleCategory(originalIndex, category)
            }
          }}
          disabled={disabled}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: isLeaf || disabled ? 'default' : 'pointer',
            width: 24,
          }}
        >
          {isLeaf ? '•' : isExpanded ? '▾' : '▸'}
        </button>

        <input
          type="radio"
          name={`category-selection-${originalIndex}`}
          checked={isSelected}
          onChange={() => {
            if (!disabled) {
              setCurrentNonWlmCat(category)
            }
          }}
          disabled={disabled}
        />

        <button
          type="button"
          onClick={() => {
            if (!disabled) {
              setCurrentNonWlmCat(category)
            }
          }}
          disabled={disabled}
          style={{
            border: 'none',
            background: 'transparent',
            cursor: disabled ? 'default' : 'pointer',
            textAlign: 'left',
            padding: 0,
            fontWeight: isSelected ? 600 : 400,
            color: 'var(--primary)',
            fontSize: 12,
          }}
        >
          {category.replace(/\+/g, ' ')}
        </button>
      </div>

      {isExpanded && children.length > 0 && (
        <div style={{ marginTop: 4 }}>
          {children.map((child: string, i: number) => (
            <CategoryTree
              key={`${child}-${i}`}
              category={child}
              categoryChildren={categoryChildren}
              expandedCategories={expandedCategories}
              toggleCategory={toggleCategory}
              setCurrentNonWlmCat={setCurrentNonWlmCat}
              currentNonWlmCat={currentNonWlmCat}
              level={level + 1}
              originalIndex={originalIndex}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function getMonumentImageTitles(monument: Monument) {
  const pictures = monument.pictures
  const titles = pictures
    .map((picture) => picture.image_title)
    .map((title) => {
      const titleParts = title.split('.')
      const extension = titleParts[titleParts.length - 1]
      return title.replace('File:', '').replace(`.${extension}`, '')
    })
  return titles
}

function ImageFile({ image }: { image: any }) {
  const url = useMemo(() => {
    if (image.file) {
      return URL.createObjectURL(image.file)
    }
    return null
  }, [image.file])

  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url)
    }
  }, [url])

  if (!url) {
    return null
  }

  return (
    <div
      className={styles.ImageToUpload}
      key={image.file?.name}
      style={{
        backgroundImage: `url("${url}")`,
      }}
    />
  )
}

function hasLocation(image: any) {
  return (
    image.latitude !== null &&
    image.longitude !== null &&
    image.latitude !== undefined &&
    image.longitude !== undefined
  )
}

interface BlockUploadProps {
  uploadOpen: boolean
  setUploadOpen: (uploadOpen: boolean) => void
  fileList: FileList | null
  setFileList: (file: FileList | null) => void
  monument: Monument | null
}

export interface ImageInfo {
  title: string
  description: string
  file: File | null
  date: string
  latitude: number | null
  longitude: number | null
  heading: number | null
  monument_id?: number
  film?: string
  typology_view_exterior?: boolean
  typology_view_interior?: boolean
  uploaded?: boolean
  extraCategories?: string[]
}

const BlockUploadFormik = ({
  uploadOpen,
  setUploadOpen,
  fileList,
  setFileList,
  monument,
}: BlockUploadProps) => {
  const { t } = useTranslation()

  const swiperRef = useRef<SwiperRef>()
  const inputFileRef = useRef<HTMLInputElement>(null)

  const activeGeocontext = getActiveGeocontext()

  const activeContest = useMemo(() => {
    if (!activeGeocontext) return null
    if (!activeGeocontext.next_contest) return null
    if (activeGeocontext.next_contest.active) {
      return activeGeocontext.next_contest
    }
    return null
  }, [activeGeocontext])

  const anyContest = useMemo(() => activeContest, [activeContest])

  const templates = useMemo(() => {
    if (monument) {
      return monument.categories_urls?.templates ?? []
    }
    return []
  }, [monument])

  const [slideActive, setSlideActive] = useState<number>(0)
  const [responseUploadOpen, setResponseUploadOpen] = useState<boolean>(false)
  const [mappedErrors, setMappedErrors] = useState<Record<number, any>>({})
  const [uploadWizard, setUploadWizard] = useState<boolean | object>(false)
  const [checkedCopia, setCheckedCopia] = useState<boolean>(false)
  const [checkedExtraCategoriesCopy, setCheckedExtraCategoriesCopy] =
    useState<boolean>(false)
  const [checkedCategoryCopy, setCheckedCategoryCopy] = useState<boolean>(false)

  const [expandedCategoriesByImage, setExpandedCategoriesByImage] = useState<
    Record<number, Record<string, boolean>>
  >({})
  const [categoryChildren, setCategoryChildren] = useState<
    Record<string, string[]>
  >({})
  const [currentNonWlmCats, setCurrentNonWlmCats] = useState<string[]>([])
  const [focusedCategoryRoots, setFocusedCategoryRoots] = useState<
    Record<number, string | null>
  >({})

  const imageTitles = useMemo(() => {
    if (monument) {
      return getMonumentImageTitles(monument)
    }
    return []
  }, [monument])

  const validationSchema = useMemo(() => {
    const requiredMessage = t('UPL_WIN_MSG_REQUIRED_FIELD')
    const titleAlreadyExistsMessage = t('UPL_WIN_MSG_DUPLICATE_TITLE')

    const imageShape: Record<string, any> = {
      title: Yup.string()
        .required(requiredMessage)
        .notOneOf(imageTitles, titleAlreadyExistsMessage),
      description: Yup.string().required(requiredMessage),
      date: Yup.string().required(requiredMessage),
      extraCategories: Yup.array().of(Yup.string()),
    }

    if (activeGeocontext?.show_typology_view_field) {
      imageShape.typology_view_exterior = Yup.boolean()
      imageShape.typology_view_interior = Yup.boolean()
    }

    return Yup.object().shape({
      images: Yup.array().of(Yup.object().shape(imageShape)),
    })
  }, [t, imageTitles, activeGeocontext])

  const queryClient = useQueryClient()
  const isMobile = isBrowserMobile()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { token, user } = useAuthUser()

  const normalizeImages = useCallback((images: ImageInfo[]) => {
    return images.map((image) => ({
      ...image,
      uploaded: false,
      extraCategories:
        image.extraCategories && image.extraCategories.length > 0
          ? image.extraCategories
          : [],
    }))
  }, [])

  const getImageCategories = useCallback((image: ImageInfo) => {
    const extras =
      image.extraCategories?.filter((category) => category.trim().length > 0) ??
      []

    return [...extras]
  }, [])

  const formik = useFormik({
    initialValues: {
      images: [] as ImageInfo[],
    },
    validationSchema,
    onSubmit: async (values, { setErrors, resetForm, setFieldValue }) => {
      const imagesToUpload = values.images
        .map((image, index) => ({
          image,
          index,
        }))
        .filter(({ image }) => !image.uploaded)

      try {
        setIsLoading(true)

        const payloadImages = imagesToUpload.map(({ image }) => ({
          ...image,
          categories: getImageCategories(image),
        }))

        const payloadNonWlmCategories = imagesToUpload.map(
          ({ index }) => currentNonWlmCats[index] ?? ''
        )

        const response = await uploadImages(
          payloadImages,
          isMobile ? 'mobile' : 'desktop',
          token,
          payloadNonWlmCategories
        )

        setIsLoading(false)

        if (response.status === 200) {
          setUploadOpen(false)
          setResponseUploadOpen(true)
          setFileList(null)
          setMappedErrors({})
          queryClient.invalidateQueries({ queryKey: ['monument'] })
          queryClient.invalidateQueries({ queryKey: ['monuments'] })
          queryClient.invalidateQueries({ queryKey: ['infiniteMonuments'] })
          resetForm()
        }
      } catch (errors: any) {
        setIsLoading(false)

        if (errors.response.status === 500) {
          toast.error(errors.response.data.detail, {
            position: toast.POSITION.BOTTOM_RIGHT,
          })
        } else if (errors.response.status === 418) {
          const errorCandidates = errors.response.data ?? []

          const failedItems: Array<{
            image: ImageInfo
            error: any
            category: string
          }> = []

          imagesToUpload.forEach(
            ({ image, index: originalIndex }, filteredIndex) => {
              const errorCandidate = errorCandidates[filteredIndex]

              if (!errorCandidate) {
                return
              }

              if (!errorCandidate.upload) {
                failedItems.push({
                  image: {
                    ...image,
                    uploaded: false,
                  },
                  error: errorCandidate,
                  category: currentNonWlmCats[originalIndex] ?? '',
                })
              }
            }
          )

          const nextImages = failedItems.map((item) => item.image)
          const nextMappedErrors = failedItems.reduce<Record<number, any>>(
            (acc, item, index) => {
              acc[index] = item.error
              return acc
            },
            {}
          )
          const nextCategories = failedItems.map((item) => item.category)

          setMappedErrors(nextMappedErrors)
          setCurrentNonWlmCats(nextCategories)
          setSlideActive(0)
          setFieldValue('images', nextImages)

          requestAnimationFrame(() => {
            swiperRef.current?.update()
            swiperRef.current?.slideTo(0)
          })

          const uploadedCount = imagesToUpload.length - nextImages.length

          if (uploadedCount > 0) {
            toast.success(t('UPL_WIN_MSG_PARTIAL_UPLOAD'), {
              position: toast.POSITION.TOP_RIGHT,
            })
          }
        } else if (errors.response.status === 400) {
          const newErrors = Object.assign({}, errors.response.data)
          const keys = Object.keys(newErrors)

          keys.forEach((key) => {
            const newValue = newErrors[key].map((v: any) => {
              if (v['non_field_errors']) {
                return { title: v['non_field_errors'] }
              }
              return v
            })
            newErrors[key] = newValue
          })

          setErrors(newErrors)
        } else {
          setErrors(errors.response.data)
        }
      }
    },
  })

  const { values, errors, touched, setFieldValue, handleChange } = formik

  const visibleImages = useMemo(() => {
    return values.images.map((image, originalIndex) => ({
      image,
      originalIndex,
    }))
  }, [values.images])

  const currentVisibleImage = useMemo(() => {
    return visibleImages[slideActive]?.image ?? null
  }, [visibleImages, slideActive])

  const currentVisibleImageIndex = useMemo(() => {
    return visibleImages[slideActive]?.originalIndex ?? null
  }, [visibleImages, slideActive])

  const currentUploadWizardCategories = useMemo(() => {
    const baseCategories =
      currentVisibleImageIndex !== null
        ? [currentNonWlmCats[currentVisibleImageIndex] ?? '']
        : [...(monument?.categories_urls?.non_wlm_categories ?? [])]

    const contestCategories =
      anyContest && monument?.in_contest
        ? (monument?.categories_urls?.wlm_categories ?? [])
        : []

    const imageCategories = currentVisibleImage
      ? getImageCategories(currentVisibleImage)
      : []

    return Array.from(
      new Set(
        [...baseCategories, ...contestCategories, ...imageCategories].filter(
          (category) => category.trim().length > 0
        )
      )
    ).join('|')
  }, [
    currentVisibleImage,
    currentVisibleImageIndex,
    currentNonWlmCats,
    monument,
    anyContest,
    getImageCategories,
  ])

  useEffect(() => {
    if (fileList && monument) {
      processUploadFiles(
        fileList,
        monument,
        activeGeocontext?.app_categories,
        activeGeocontext?.default_format_of_image_name
      ).then((images) => {
        const normalizedImages = normalizeImages(images)
        setFieldValue('images', normalizedImages)

        const initialCat =
          monument.categories_urls?.non_wlm_categories?.[0] ?? ''
        setCurrentNonWlmCats(Array(normalizedImages.length).fill(initialCat))
        setMappedErrors({})
        setSlideActive(0)
        setFocusedCategoryRoots({})
        setExpandedCategoriesByImage({})

        requestAnimationFrame(() => {
          swiperRef.current?.update()
          swiperRef.current?.slideTo(0)
        })
      })
    }
  }, [fileList, monument, activeGeocontext, setFieldValue, normalizeImages])

  useEffect(() => {
    if (visibleImages.length === 0) {
      setSlideActive(0)
      requestAnimationFrame(() => {
        swiperRef.current?.update()
      })
      return
    }

    if (slideActive >= visibleImages.length) {
      const newIndex = visibleImages.length - 1
      setSlideActive(newIndex)

      requestAnimationFrame(() => {
        swiperRef.current?.update()
        swiperRef.current?.slideTo(newIndex)
      })
      return
    }

    requestAnimationFrame(() => {
      swiperRef.current?.update()
    })
  }, [visibleImages.length, slideActive])

  const copyTitles = useCallback(
    (title: string) => {
      const position = title.indexOf('_001')
      let titleWithoutNumber = title

      if (position > -1) {
        titleWithoutNumber = title.substring(0, position)
      }

      values.images.forEach((_, index: number) => {
        if (index !== 0 && !values.images[index].uploaded) {
          const suffix = String(index + 1).padStart(3, '0')
          const newTitle = `${titleWithoutNumber}_${suffix}`
          setFieldValue(`images[${index}].title`, newTitle)
        }
      })
    },
    [values.images, setFieldValue]
  )

  const copyDescription = useCallback(
    (description: string) => {
      values.images.forEach((_, index: number) => {
        if (index !== 0 && !values.images[index].uploaded) {
          setFieldValue(`images[${index}].description`, description)
        }
      })
    },
    [values.images, setFieldValue]
  )

  const copyDate = useCallback(
    (date: string) => {
      values.images.forEach((_, index: number) => {
        if (index !== 0 && !values.images[index].uploaded) {
          setFieldValue(`images[${index}].date`, date)
        }
      })
    },
    [values.images, setFieldValue]
  )

  const copyFilm = useCallback(
    (film: string) => {
      values.images.forEach((_, index: number) => {
        if (index !== 0 && !values.images[index].uploaded) {
          setFieldValue(`images[${index}].film`, film)
        }
      })
    },
    [values.images, setFieldValue]
  )

  const copyTypologyView = useCallback(
    (exterior: boolean, interior: boolean) => {
      values.images.forEach((_, index: number) => {
        if (index !== 0 && !values.images[index].uploaded) {
          setFieldValue(`images[${index}].typology_view_exterior`, exterior)
          setFieldValue(`images[${index}].typology_view_interior`, interior)
        }
      })
    },
    [values.images, setFieldValue]
  )

  const copyExtraCategories = useCallback(
    (extraCategories: string[]) => {
      const normalized = [...extraCategories]

      values.images.forEach((image, index: number) => {
        if (index !== 0 && !image.uploaded) {
          const current = image.extraCategories ?? []
          const isSame =
            current.length === normalized.length &&
            current.every((value, i) => value === normalized[i])

          if (!isSame) {
            setFieldValue(`images[${index}].extraCategories`, normalized)
          }
        }
      })
    },
    [values.images, setFieldValue]
  )

  const copySelectedCategory = useCallback(
    (category: string) => {
      setCurrentNonWlmCats((prev) =>
        prev.map((value, index) => {
          if (index === 0) return value
          if (values.images[index]?.uploaded) return value
          return category
        })
      )
    },
    [values.images]
  )

  const copyCategoryTreeStateFromFirstImage = useCallback(() => {
    setFocusedCategoryRoots((prev) => {
      const sourceRoot = prev[0] ?? null
      const next = { ...prev }

      values.images.forEach((image, index) => {
        if (!image.uploaded) {
          next[index] = sourceRoot
        }
      })

      return next
    })

    setExpandedCategoriesByImage((prev) => {
      const sourceTree = prev[0] ?? {}
      const next = { ...prev }

      values.images.forEach((image, index) => {
        if (!image.uploaded) {
          next[index] = { ...sourceTree }
        }
      })

      return next
    })
  }, [values.images])

  useEffect(() => {
    if (!checkedCopia) return
    if (values.images.length > 0) {
      copyTitles(values.images[0].title)
    }
  }, [values.images, checkedCopia, copyTitles])

  useEffect(() => {
    if (!checkedCategoryCopy) return
    if (values.images.length > 0 && currentNonWlmCats[0]) {
      copySelectedCategory(currentNonWlmCats[0])
      copyCategoryTreeStateFromFirstImage()
    }
  }, [
    checkedCategoryCopy,
    currentNonWlmCats,
    values.images.length,
    copySelectedCategory,
    copyCategoryTreeStateFromFirstImage,
  ])

  const descriptionForWizard = useMemo(() => {
    if (!monument || values.images.length === 0) return ''
    return `${values.images[0].description} {{Load via app ${
      activeGeocontext?.app_domain
    }|year=${dayjs().year()}|source=wizard}}`
  }, [values.images, monument, activeGeocontext])

  useEffect(() => {
    if (!uploadOpen) {
      setFileList(null)
      setSlideActive(0)
      setMappedErrors({})
      setCheckedCopia(false)
      setCheckedExtraCategoriesCopy(false)
      setCheckedCategoryCopy(false)
      setExpandedCategoriesByImage({})
      setCategoryChildren({})
      setCurrentNonWlmCats([])
      setFocusedCategoryRoots({})
      queryClient.invalidateQueries({ queryKey: ['monument'] })
      queryClient.invalidateQueries({ queryKey: ['monuments'] })
      queryClient.invalidateQueries({ queryKey: ['infiniteMonuments'] })
      if (swiperRef.current) {
        swiperRef.current.slideTo(0)
        swiperRef.current.update()
      }
    }
  }, [uploadOpen, setFileList, queryClient])

  const toggleCategory = useCallback(
    async (originalIndex: number, category: string) => {
      const currentExpanded =
        expandedCategoriesByImage[originalIndex]?.[category] ?? false

      if (!currentExpanded) {
        if (!Object.prototype.hasOwnProperty.call(categoryChildren, category)) {
          const data = await categoryTree(category, token!)
          setCategoryChildren((prev) => ({
            ...prev,
            [category]: data ?? [],
          }))
        }
      }

      setExpandedCategoriesByImage((prev) => {
        const nextForCurrentImage = {
          ...(prev[originalIndex] ?? {}),
          [category]: !currentExpanded,
        }

        if (checkedCategoryCopy) {
          const next = { ...prev }

          values.images.forEach((image, index) => {
            if (!image.uploaded) {
              next[index] = { ...nextForCurrentImage }
            }
          })

          return next
        }

        return {
          ...prev,
          [originalIndex]: nextForCurrentImage,
        }
      })
    },
    [
      expandedCategoriesByImage,
      categoryChildren,
      token,
      checkedCategoryCopy,
      values.images,
    ]
  )

  const confirmCategoryTreeRoot = useCallback(
    async (originalIndex: number) => {
      const selectedCategory = currentNonWlmCats[originalIndex]
      if (!selectedCategory) return

      if (
        !Object.prototype.hasOwnProperty.call(
          categoryChildren,
          selectedCategory
        )
      ) {
        const data = await categoryTree(selectedCategory, token!)
        setCategoryChildren((prev) => ({
          ...prev,
          [selectedCategory]: data ?? [],
        }))
      }

      setFocusedCategoryRoots((prev) => {
        if (checkedCategoryCopy) {
          const next = { ...prev }
          values.images.forEach((image, index) => {
            if (!image.uploaded) {
              next[index] = selectedCategory
            }
          })
          return next
        }

        return {
          ...prev,
          [originalIndex]: selectedCategory,
        }
      })

      setExpandedCategoriesByImage((prev) => {
        const nextTree = {
          ...(prev[originalIndex] ?? {}),
          [selectedCategory]: true,
        }

        if (checkedCategoryCopy) {
          const next = { ...prev }
          values.images.forEach((image, index) => {
            if (!image.uploaded) {
              next[index] = { ...nextTree }
            }
          })
          return next
        }

        return {
          ...prev,
          [originalIndex]: nextTree,
        }
      })
    },
    [
      currentNonWlmCats,
      categoryChildren,
      token,
      checkedCategoryCopy,
      values.images,
    ]
  )

  const resetCategoryTreeRoot = useCallback(
    (originalIndex: number) => {
      const firstCategory =
        monument?.categories_urls?.non_wlm_categories?.[0] ?? ''

      setCurrentNonWlmCats((prev) => {
        if (checkedCategoryCopy) {
          return prev.map((value, index) => {
            if (values.images[index]?.uploaded) return value
            return firstCategory
          })
        }

        const next = [...prev]
        next[originalIndex] = firstCategory
        return next
      })

      setFocusedCategoryRoots((prev) => {
        if (checkedCategoryCopy) {
          const next = { ...prev }

          values.images.forEach((image, index) => {
            if (!image.uploaded) {
              next[index] = null
            }
          })

          return next
        }

        return {
          ...prev,
          [originalIndex]: null,
        }
      })

      setExpandedCategoriesByImage((prev) => {
        if (checkedCategoryCopy) {
          const next: Record<number, Record<string, boolean>> = { ...prev }

          values.images.forEach((image, index) => {
            if (!image.uploaded) {
              next[index] = {}
            }
          })

          return next
        }

        return {
          ...prev,
          [originalIndex]: {},
        }
      })
    },
    [checkedCategoryCopy, values.images, monument]
  )

  const nonWlmCategoriesTree = useMemo(() => {
    if (!monument) return []
    return (
      monument.categories_urls?.non_wlm_categories.map((c) => ({
        name: c,
        children: [],
      })) ?? []
    )
  }, [monument])

  useEffect(() => {
    if (!monument?.categories_urls?.non_wlm_categories) return
    if (values.images.length === 0) return

    const initialCat = monument.categories_urls.non_wlm_categories[0] ?? ''

    setCurrentNonWlmCats((prev) => {
      if (prev.length === values.images.length) return prev
      return values.images.map((_, index) => prev[index] ?? initialCat)
    })
  }, [monument, values.images.length])

  const swiperKey = useMemo(() => {
    return `swiper-${values.images.length}`
  }, [values.images.length])

  return (
    <>
      <div
        className={styles.ModalUpload}
        style={{
          opacity: uploadOpen ? 1 : 0,
          pointerEvents: uploadOpen ? 'all' : 'none',
          transition: 'all 0.5s ease-in-out',
        }}
      >
        <div className={styles.ModalUploadContainer}>
          <form onSubmit={formik.handleSubmit}>
            <div
              className="d-flex py-2 align-items-center justify-content-between position-sticky"
              style={{
                top: 0,
                zIndex: 100,
                backgroundColor: 'var(--overlay)',
              }}
            >
              <div className="d-flex">
                <div className={styles.TitleConcorso}>
                  {user && !user?.wlm_email_verified ? (
                    <div className="text-danger">
                      ⚠️ {t('UPL_WIN_MSG_MAIL_ALERT')}
                    </div>
                  ) : monument?.in_contest ? (
                    activeContest ? (
                      t('UPL_WIN_MSG_CONTEST_ON')
                    ) : (
                      t('UPL_WIN_MSG_CONTEST_OFF')
                    )
                  ) : (
                    t('UPL_WIN_MSG_CONTEST_OUT')
                  )}
                </div>
              </div>

              <div className="d-flex align-items-center">
                <div
                  className={styles.LabelUploadWizard}
                  onClick={() => {
                    const wizardData: Record<string, any> = {
                      description: descriptionForWizard,
                      descriptionlang: activeGeocontext?.language_code,
                      categories: currentUploadWizardCategories,
                    }

                    if (monument?.in_contest && anyContest) {
                      wizardData.campaign = `wlm-${activeGeocontext?.country_code}`
                      wizardData.id = monument?.wlm_n
                    }

                    setUploadWizard(wizardData)
                  }}
                >
                  {t('GENERIC_SWITCH_TO_UPLOADWIZARD')}
                </div>

                <div className={styles.Close}>
                  <Close
                    onClick={() => {
                      setFileList(null)
                      setUploadOpen(false)
                    }}
                  />
                </div>
              </div>
            </div>

            <FormikProvider value={formik}>
              <FieldArray
                name="images"
                render={({ remove }: { remove: (index: number) => void }) => (
                  <Swiper
                    key={swiperKey}
                    slidesPerView={
                      isMobile || visibleImages.length === 1 ? 1 : 1.1
                    }
                    spaceBetween={
                      isMobile || visibleImages.length === 1 ? 0 : 20
                    }
                    onSwiper={(swiper) => {
                      swiperRef.current = swiper
                    }}
                    onSlideChange={(swiper) => {
                      setSlideActive(swiper.activeIndex)
                    }}
                    className={styles.Swiper}
                  >
                    {visibleImages.map(({ image, originalIndex }, index) => {
                      const focusedRoot = focusedCategoryRoots[originalIndex]
                      const categoriesToRender = focusedRoot
                        ? [{ name: focusedRoot, children: [] }]
                        : nonWlmCategoriesTree

                      const isCategorySelectionDisabled =
                        index !== 0 && checkedCategoryCopy

                      return (
                        <SwiperSlide key={originalIndex}>
                          <div className={styles.CardImageToUpload}>
                            <div className="d-flex align-items-center justify-content-between">
                              <div className={styles.TitleUpload}>
                                {t('UPL_WIN_MAIN_TITLE')}:{' '}
                                <strong>{monument?.label}</strong>
                              </div>

                              {visibleImages.length > 1 && (
                                <div
                                  className="pointer"
                                  onClick={() => remove(originalIndex)}
                                >
                                  <DeleteImage />
                                </div>
                              )}
                            </div>

                            <div>
                              {image.file && <ImageFile image={image} />}

                              {mappedErrors[originalIndex] && (
                                <div
                                  className={`alert alert-danger ${styles.ErrorMapped}`}
                                >
                                  {typeof mappedErrors[originalIndex] ===
                                  'string'
                                    ? mappedErrors[originalIndex]
                                    : JSON.stringify(
                                        mappedErrors[originalIndex]
                                      )}
                                </div>
                              )}
                            </div>

                            <div className="mt-4">
                              <div className="d-flex align-items-center justify-content-between">
                                <div
                                  className={`${styles.LabelInput} d-flex align-items-center`}
                                >
                                  {t('UPL_WIN_PHOTO_NAME_TITLE')} <span>*</span>
                                  <span
                                    onClick={() => {
                                      copyTitles(
                                        values.images[originalIndex].title
                                      )
                                    }}
                                  >
                                    {index === 0 &&
                                      visibleImages.length > 1 && (
                                        <div className="d-flex align-items-center ms-4">
                                          <input
                                            type="checkbox"
                                            className="me-2"
                                            checked={checkedCopia}
                                            onChange={() => {
                                              setCheckedCopia(!checkedCopia)
                                            }}
                                          />
                                          {t('UPL_WIN_CMD_DUPLICATE')}
                                        </div>
                                      )}
                                  </span>
                                </div>
                              </div>

                              <div>
                                <textarea
                                  rows={isMobile ? 2 : 1}
                                  className={styles.InputTitle}
                                  name={`images[${originalIndex}].title`}
                                  disabled={
                                    isLoading || (index !== 0 && checkedCopia)
                                  }
                                  value={values.images[originalIndex].title}
                                  onChange={handleChange}
                                  placeholder={t('UPL_WIN_PHOTO_NAME_HINT')}
                                  style={{
                                    boxShadow:
                                      getIn(
                                        touched,
                                        `images[${originalIndex}].title`
                                      ) &&
                                      getIn(
                                        errors,
                                        `images[${originalIndex}].title`
                                      )
                                        ? '0px 0px 0px 1px var(--tertiary)'
                                        : '0px 0px 0px 1px #E5E5E5',
                                  }}
                                />
                              </div>

                              {getIn(
                                touched,
                                `images[${originalIndex}].title`
                              ) &&
                                getIn(
                                  errors,
                                  `images[${originalIndex}].title`
                                ) && (
                                  <div className={styles.Error}>
                                    {getIn(
                                      errors,
                                      `images[${originalIndex}].title`
                                    )}
                                  </div>
                                )}
                            </div>

                            <div className="mt-2">
                              <div
                                className={`${styles.LabelInput} d-flex align-items-center`}
                              >
                                {t('UPL_WIN_DESCRIPTION_TITLE')} <span>*</span>
                                {index === 0 && visibleImages.length > 1 && (
                                  <button
                                    type="button"
                                    className={styles.ButtonDuplicate}
                                    onClick={() => {
                                      copyDescription(
                                        values.images[originalIndex].description
                                      )
                                    }}
                                  >
                                    {t('UPL_WIN_CMD_DUPLICATE')}
                                  </button>
                                )}
                              </div>

                              <div>
                                <textarea
                                  className={styles.InputTitle}
                                  rows={2}
                                  disabled={isLoading}
                                  name={`images[${originalIndex}].description`}
                                  value={
                                    values.images[originalIndex].description
                                  }
                                  onChange={handleChange}
                                  placeholder={t('UPL_WIN_DESCRIPTION_HINT')}
                                  style={{
                                    boxShadow:
                                      getIn(
                                        touched,
                                        `images[${originalIndex}].description`
                                      ) &&
                                      getIn(
                                        errors,
                                        `images[${originalIndex}].description`
                                      )
                                        ? '0px 0px 0px 1px #FF0000'
                                        : '0px 0px 0px 1px #E5E5E5',
                                  }}
                                />

                                {getIn(
                                  touched,
                                  `images[${originalIndex}].description`
                                ) &&
                                  getIn(
                                    errors,
                                    `images[${originalIndex}].description`
                                  ) && (
                                    <div className={styles.Error}>
                                      {getIn(
                                        errors,
                                        `images[${originalIndex}].description`
                                      )}
                                    </div>
                                  )}
                              </div>
                            </div>

                            <div className="mt-2">
                              <div
                                className={`${styles.LabelInput} d-flex align-items-center`}
                              >
                                {t('UPL_WIN_DATE_TITLE')}
                                <span>*</span>

                                {index === 0 && visibleImages.length > 1 && (
                                  <button
                                    type="button"
                                    className={styles.ButtonDuplicate}
                                    onClick={() => {
                                      copyDate(
                                        values.images[originalIndex].date
                                      )
                                    }}
                                  >
                                    {t('UPL_WIN_CMD_DUPLICATE')}
                                  </button>
                                )}
                              </div>

                              <div>
                                <input
                                  style={{
                                    boxShadow:
                                      getIn(
                                        touched,
                                        `images[${originalIndex}].date`
                                      ) &&
                                      getIn(
                                        errors,
                                        `images[${originalIndex}].date`
                                      )
                                        ? '0px 0px 0px 1px #FF0000'
                                        : '0px 0px 0px 1px #E5E5E5',
                                  }}
                                  disabled={isLoading}
                                  className={styles.InputTitle}
                                  name={`images[${originalIndex}].date`}
                                  value={values.images[originalIndex].date}
                                  onChange={handleChange}
                                  placeholder={t('UPL_WIN_DATE_HINT')}
                                />

                                {getIn(
                                  touched,
                                  `images[${originalIndex}].date`
                                ) &&
                                  getIn(
                                    errors,
                                    `images[${originalIndex}].date`
                                  ) && (
                                    <div className={styles.Error}>
                                      {getIn(
                                        errors,
                                        `images[${originalIndex}].date`
                                      )}
                                    </div>
                                  )}
                              </div>
                            </div>

                            {activeGeocontext?.show_typology_view_field && (
                              <div className="mt-2">
                                <div
                                  className={`${styles.LabelInput} d-flex align-items-center mb-1`}
                                >
                                  {t('UPL_WIN_EXTRA_INFO_VIEW_TYPE')}

                                  {index === 0 && visibleImages.length > 1 && (
                                    <button
                                      type="button"
                                      className={styles.ButtonDuplicate}
                                      onClick={() => {
                                        copyTypologyView(
                                          values.images[originalIndex]
                                            .typology_view_exterior || false,
                                          values.images[originalIndex]
                                            .typology_view_interior || false
                                        )
                                      }}
                                    >
                                      {t('UPL_WIN_CMD_DUPLICATE')}
                                    </button>
                                  )}
                                </div>

                                <div>
                                  <div className="d-flex align-items-center">
                                    <div className="d-flex align-items-center me-4">
                                      <input
                                        type="checkbox"
                                        name={`images[${originalIndex}].typology_view_exterior`}
                                        value="exterior"
                                        onChange={(e) => {
                                          setFieldValue(
                                            `images[${originalIndex}].typology_view_exterior`,
                                            e.target.checked
                                          )
                                        }}
                                        checked={
                                          !!values.images[originalIndex]
                                            .typology_view_exterior
                                        }
                                      />
                                      <div
                                        className={`${styles.LabelInput} ms-2 d-flex align-items-center`}
                                      >
                                        {t(
                                          'UPL_WIN_EXTRA_INFO_VIEW_TYPE_EXTERIOR'
                                        )}
                                      </div>
                                    </div>

                                    <div className="d-flex align-items-center">
                                      <input
                                        type="checkbox"
                                        name={`images[${originalIndex}].typology_view_interior`}
                                        value="interior"
                                        onChange={(e) => {
                                          setFieldValue(
                                            `images[${originalIndex}].typology_view_interior`,
                                            e.target.checked
                                          )
                                        }}
                                        checked={
                                          !!values.images[originalIndex]
                                            .typology_view_interior
                                        }
                                      />
                                      <div
                                        className={`${styles.LabelInput} ms-2 d-flex align-items-center`}
                                      >
                                        {t(
                                          'UPL_WIN_EXTRA_INFO_VIEW_TYPE_INTERIOR'
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {activeGeocontext?.show_film_field && (
                              <div className="mt-2">
                                <div
                                  className={`${styles.LabelInput} d-flex align-items-center`}
                                >
                                  {t('UPL_WIN_EXTRA_INFO_FILM_QUESTION')}

                                  {index === 0 && visibleImages.length > 1 && (
                                    <button
                                      type="button"
                                      className={styles.ButtonDuplicate}
                                      onClick={() => {
                                        copyFilm(
                                          values.images[originalIndex].film ||
                                            ''
                                        )
                                      }}
                                    >
                                      {t('UPL_WIN_CMD_DUPLICATE')}
                                    </button>
                                  )}
                                </div>

                                <div>
                                  <textarea
                                    className={styles.InputTitle}
                                    rows={4}
                                    disabled={isLoading}
                                    name={`images[${originalIndex}].film`}
                                    value={
                                      values.images[originalIndex].film || ''
                                    }
                                    onChange={handleChange}
                                    placeholder={t(
                                      'UPL_WIN_EXTRA_INFO_FILM_ANSWER'
                                    )}
                                    style={{
                                      boxShadow:
                                        getIn(
                                          touched,
                                          `images[${originalIndex}].film`
                                        ) &&
                                        getIn(
                                          errors,
                                          `images[${originalIndex}].film`
                                        )
                                          ? '0px 0px 0px 1px #FF0000'
                                          : '0px 0px 0px 1px #E5E5E5',
                                    }}
                                  />
                                  {getIn(
                                    touched,
                                    `images[${originalIndex}].film`
                                  ) &&
                                    getIn(
                                      errors,
                                      `images[${originalIndex}].film`
                                    ) && (
                                      <div className={styles.Error}>
                                        {getIn(
                                          errors,
                                          `images[${originalIndex}].film`
                                        )}
                                      </div>
                                    )}
                                </div>
                              </div>
                            )}
                          </div>

                          {((templates && templates.length > 0) ||
                            (activeGeocontext?.feed_commons_with_exif_coordinates &&
                              hasLocation(values.images[index]))) && (
                            <div className={styles.CardImageToUpload}>
                              <span className={styles.Categorie}>
                                <div className="d-flex align-items-center justify-content-center mb-2">
                                  <strong>{t('UPL_WIN_TEMPLATE_TITLE')}</strong>
                                </div>
                                {templates.map((c, i) => (
                                  <div className="1" key={i}>
                                    {c.replace(/\+/g, ' ')}
                                  </div>
                                ))}
                                {activeGeocontext?.feed_commons_with_exif_coordinates &&
                                  hasLocation(values.images[index]) && (
                                    <div className="py-1">
                                      {values.images[index].heading &&
                                        `{{Location:${values.images[index].latitude}|${values.images[index].longitude}|heading:${values.images[index].heading}}}`}
                                      {!values.images[index].heading &&
                                        `{{Location:${values.images[index].latitude}|${values.images[index].longitude}}}`}
                                    </div>
                                  )}
                              </span>
                            </div>
                          )}

                          <div className={styles.CardImageToUpload}>
                            <span className={styles.Categorie}>
                              <div className="d-flex align-items-center justify-content-center mb-2">
                                <strong>{t('UPL_WIN_CATEGORY_TITLE')}</strong>
                              </div>
                              <div
                                className={`${styles.LabelInput} d-flex align-items-center`}
                              >
                                {t('UPL_WIN_BASE_CATEGORY_TITLE')}
                                {index === 0 && visibleImages.length > 1 && (
                                  <div className="d-flex align-items-center ms-4">
                                    <input
                                      type="checkbox"
                                      className="me-2"
                                      checked={checkedCategoryCopy}
                                      onChange={(e) => {
                                        const checked = e.target.checked
                                        setCheckedCategoryCopy(checked)

                                        if (checked) {
                                          const selectedCategory =
                                            currentNonWlmCats[0]
                                          if (selectedCategory) {
                                            copySelectedCategory(
                                              selectedCategory
                                            )
                                          }
                                          copyCategoryTreeStateFromFirstImage()
                                        }
                                      }}
                                    />
                                    {t('UPL_WIN_CMD_DUPLICATE')}
                                  </div>
                                )}

                                <button
                                  type="button"
                                  className={styles.ButtonDuplicate}
                                  onClick={() =>
                                    confirmCategoryTreeRoot(originalIndex)
                                  }
                                  disabled={!currentNonWlmCats[originalIndex]}
                                  style={{ marginLeft: 8 }}
                                >
                                  {t('UPL_WIN_CMD_CONFIRM_CATEGORY')}
                                </button>

                                <button
                                  type="button"
                                  className={styles.ButtonCancel}
                                  onClick={() =>
                                    resetCategoryTreeRoot(originalIndex)
                                  }
                                  disabled={
                                    !focusedCategoryRoots[originalIndex]
                                  }
                                  style={{
                                    marginLeft: 8,
                                    maxWidth: 'fit-content',
                                  }}
                                >
                                  {t('UPL_WIN_CMD_RESET_CATEGORY')}
                                </button>
                              </div>

                              <div
                                style={{
                                  marginTop: 12,
                                  pointerEvents: isCategorySelectionDisabled
                                    ? 'none'
                                    : 'auto',
                                }}
                              >
                                {categoriesToRender.map((c, i) => (
                                  <CategoryTree
                                    key={`category-tree-${originalIndex}-${c.name}-${i}`}
                                    toggleCategory={toggleCategory}
                                    category={c.name}
                                    categoryChildren={categoryChildren}
                                    expandedCategories={
                                      expandedCategoriesByImage[
                                        originalIndex
                                      ] ?? {}
                                    }
                                    currentNonWlmCat={
                                      currentNonWlmCats[originalIndex]
                                    }
                                    originalIndex={originalIndex}
                                    disabled={isCategorySelectionDisabled}
                                    setCurrentNonWlmCat={(category) => {
                                      if (isCategorySelectionDisabled) return

                                      setCurrentNonWlmCats((prev) => {
                                        const newState = [...prev]
                                        newState[originalIndex] = category
                                        return newState
                                      })

                                      if (index === 0 && checkedCategoryCopy) {
                                        copySelectedCategory(category)
                                      }
                                    }}
                                  />
                                ))}
                              </div>

                              {/* show only if contest is active or monument is in contest */}

                              {monument?.in_contest &&
                                anyContest &&
                                monument?.categories_urls?.wlm_categories?.map(
                                  (c, i) => (
                                    <div
                                      className="py-1"
                                      style={{
                                        borderBottom:
                                          i ===
                                          monument?.categories_urls
                                            ?.wlm_categories?.length!! -
                                            1
                                            ? '1px solid var(--primary)'
                                            : 'none',
                                        fontSize: 12,
                                      }}
                                      key={`default-category-${i}`}
                                    >
                                      {c.replace(/\+/g, ' ')}
                                    </div>
                                  )
                                )}
                            </span>

                            <div className="mt-3">
                              <div className="d-flex align-items-center justify-content-between">
                                <div
                                  className={`${styles.LabelInput} d-flex align-items-center`}
                                >
                                  {t('UPL_WIN_EXTRA_CATEGORY_TITLE')}
                                  <span>
                                    {index === 0 &&
                                      visibleImages.length > 1 && (
                                        <div className="d-flex align-items-center ms-4">
                                          <input
                                            type="checkbox"
                                            className="me-2"
                                            checked={checkedExtraCategoriesCopy}
                                            onChange={(e) => {
                                              const checked = e.target.checked
                                              setCheckedExtraCategoriesCopy(
                                                checked
                                              )

                                              if (checked) {
                                                copyExtraCategories(
                                                  values.images[0]
                                                    .extraCategories ?? []
                                                )
                                              }
                                            }}
                                          />
                                          {t('UPL_WIN_CMD_DUPLICATE')}
                                        </div>
                                      )}
                                  </span>
                                </div>
                              </div>

                              <FieldArray
                                name={`images[${originalIndex}].extraCategories`}
                                render={() => (
                                  <div className="mt-2">
                                    {(
                                      values.images[originalIndex]
                                        .extraCategories ?? []
                                    ).map((_, categoryIndex) => (
                                      <div
                                        key={categoryIndex}
                                        className="d-flex align-items-center mb-2"
                                      >
                                        <input
                                          type="text"
                                          className={styles.InputTitle}
                                          disabled={
                                            isLoading ||
                                            (index !== 0 &&
                                              checkedExtraCategoriesCopy)
                                          }
                                          value={
                                            values.images[originalIndex]
                                              .extraCategories?.[
                                              categoryIndex
                                            ] ?? ''
                                          }
                                          onChange={(e) => {
                                            const newValue = e.target.value

                                            setFieldValue(
                                              `images[${originalIndex}].extraCategories[${categoryIndex}]`,
                                              newValue
                                            )

                                            if (
                                              index === 0 &&
                                              checkedExtraCategoriesCopy
                                            ) {
                                              const currentExtraCategories = [
                                                ...(values.images[originalIndex]
                                                  .extraCategories ?? []),
                                              ]
                                              currentExtraCategories[
                                                categoryIndex
                                              ] = newValue
                                              copyExtraCategories(
                                                currentExtraCategories
                                              )
                                            }
                                          }}
                                          placeholder={t(
                                            'UPL_WIN_EXTRA_CATEGORY_HINT'
                                          )}
                                        />

                                        <button
                                          type="button"
                                          className={styles.ButtonCancel}
                                          onClick={() => {
                                            const currentExtraCategories = [
                                              ...(values.images[originalIndex]
                                                .extraCategories ?? []),
                                            ]

                                            const nextExtraCategories =
                                              currentExtraCategories.filter(
                                                (_, i) => i !== categoryIndex
                                              )

                                            setFieldValue(
                                              `images[${originalIndex}].extraCategories`,
                                              nextExtraCategories
                                            )

                                            if (
                                              index === 0 &&
                                              checkedExtraCategoriesCopy
                                            ) {
                                              copyExtraCategories(
                                                nextExtraCategories
                                              )
                                            }
                                          }}
                                          disabled={
                                            (index !== 0 &&
                                              checkedExtraCategoriesCopy) ||
                                            isLoading
                                          }
                                          style={{ marginLeft: 8 }}
                                        >
                                          {t('UPL_WIN_CMD_REMOVE_CATEGORY')}
                                        </button>
                                      </div>
                                    ))}

                                    <button
                                      type="button"
                                      className={styles.ButtonDuplicate}
                                      onClick={() => {
                                        const currentExtraCategories = [
                                          ...(values.images[originalIndex]
                                            .extraCategories ?? []),
                                        ]
                                        const nextExtraCategories = [
                                          ...currentExtraCategories,
                                          '',
                                        ]

                                        setFieldValue(
                                          `images[${originalIndex}].extraCategories`,
                                          nextExtraCategories
                                        )

                                        if (
                                          index === 0 &&
                                          checkedExtraCategoriesCopy
                                        ) {
                                          copyExtraCategories(
                                            nextExtraCategories
                                          )
                                        }
                                      }}
                                      disabled={
                                        (index !== 0 &&
                                          checkedExtraCategoriesCopy) ||
                                        isLoading
                                      }
                                    >
                                      {t('UPL_WIN_CMD_ADD_CATEGORY')}
                                    </button>
                                  </div>
                                )}
                              />
                            </div>
                          </div>
                        </SwiperSlide>
                      )
                    })}
                  </Swiper>
                )}
              />
            </FormikProvider>

            {visibleImages.length > 1 && (
              <div className={styles.PaginationContainer}>
                <ArrowLeftSlideShow
                  onClick={() => {
                    if (slideActive > 0) {
                      swiperRef.current?.slidePrev()
                    }
                  }}
                  className={classNames('me-3', {
                    pointer: slideActive > 0,
                  })}
                  fill={
                    slideActive > 0
                      ? 'var(--primary)'
                      : 'var(--colori-neutri-gray-2)'
                  }
                />

                <div className={styles.CurrentSlide}>
                  {visibleImages.length > 0 ? slideActive + 1 : 0} /{' '}
                  {visibleImages.length}
                </div>

                <ArrowRightSlideShow
                  onClick={() => {
                    if (slideActive < visibleImages.length - 1) {
                      swiperRef.current?.slideNext()
                    }
                  }}
                  className={classNames('ms-3', {
                    pointer: slideActive < visibleImages.length - 1,
                  })}
                  fill={
                    slideActive < visibleImages.length - 1
                      ? 'var(--primary)'
                      : 'var(--colori-neutri-gray-2)'
                  }
                />
              </div>
            )}

            <input
              type="file"
              className="d-none"
              onChange={(e) => {
                const currentVisible = visibleImages[slideActive]
                if (
                  monument &&
                  currentVisible &&
                  e.target.files &&
                  e.target.files.length > 0
                ) {
                  const { originalIndex } = currentVisible

                  setFieldValue(`images[${originalIndex}].file`, null)

                  processUploadFiles(
                    e.target.files[0],
                    monument,
                    activeGeocontext?.app_categories
                  ).then((imageValues) => {
                    const normalizedImage = normalizeImages(imageValues)[0]
                    setFieldValue(`images[${originalIndex}]`, normalizedImage)

                    requestAnimationFrame(() => {
                      swiperRef.current?.update()
                    })
                  })
                }
              }}
              ref={inputFileRef}
              hidden={true}
              accept="image/*"
            />

            <div className={styles.ContainerButtons}>
              <button
                type="button"
                className={styles.ButtonRiseleziona}
                onClick={() => {
                  inputFileRef.current?.click()
                }}
                disabled={visibleImages.length === 0}
              >
                {t('UPL_WIN_CMD_RESELECT')}
              </button>

              <button
                type="submit"
                className={styles.ButtonUpload}
                disabled={isLoading || visibleImages.length === 0}
              >
                {isLoading && <Spinner size="sm" />}{' '}
                {t('MON_BOX_BUTTON_UPLOAD')}
              </button>
            </div>
          </form>
        </div>
      </div>

      {responseUploadOpen && !isMobile && (
        <div
          className={styles.ResponseOverlay}
          onClick={() => {
            setResponseUploadOpen(false)
          }}
        >
          <div className={styles.ResponseContainer}>
            <div
              className="position-absolute pointer"
              style={{
                top: 20,
                right: 20,
              }}
            >
              <Close />
            </div>
            <div>
              <UploadSuccess />
            </div>
            <div className={styles.UploadAvvenuto}>
              {t('UPL_WIN_MSG_SUCCESS')}
            </div>
          </div>
        </div>
      )}

      {responseUploadOpen && isMobile && (
        <div
          className={styles.ResponseOverlay}
          onClick={() => {
            setResponseUploadOpen(false)
          }}
        >
          <div className={styles.ResponseContainer}>
            <div
              className="position-absolute pointer"
              style={{
                top: 20,
                right: 20,
              }}
            >
              <Close />
            </div>
            <div>
              <UploadSuccess />
            </div>
            <div className={styles.UploadAvvenuto}>
              {t('UPL_WIN_MSG_SUCCESS')}
            </div>
          </div>
        </div>
      )}

      <Modal
        centered
        show={!!uploadWizard}
        onHide={() => setUploadWizard(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('GENERIC_SWITCH_TO_UPLOADWIZARD')}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>{t('WZD_BOX_WARNING_TEXT')}</p>
        </Modal.Body>

        <Modal.Footer>
          <button
            className={styles.ButtonCancel}
            onClick={() => setUploadWizard(false)}
          >
            {t('WZD_BOX_CANCEL_BUTTON')}
          </button>

          <button
            className={styles.ButtonUpload}
            onClick={() => {
              const params = new URLSearchParams(uploadWizard as any)
              window.open(
                `https://commons.wikimedia.org/wiki/Special:UploadWizard?${params.toString()}`,
                '_blank'
              )
              setUploadWizard(false)
              setUploadOpen(false)
              setFileList(null)
            }}
          >
            {t('GENERIC_CONTINUE')}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default BlockUploadFormik
